import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Note, Plus, Trash, X, PushPin } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = [
  { id: 'yellow', bg: '#FEF3C7', border: '#FBBF24', dark: '#92400E' },
  { id: 'green',  bg: '#D1FAE5', border: '#10B981', dark: '#065F46' },
  { id: 'pink',   bg: '#FCE7F3', border: '#EC4899', dark: '#9D174D' },
  { id: 'blue',   bg: '#DBEAFE', border: '#3B82F6', dark: '#1E40AF' },
  { id: 'orange', bg: '#FFEDD5', border: '#FB923C', dark: '#9A3412' },
];
const COLOR_BY_ID = Object.fromEntries(COLORS.map((c) => [c.id, c]));

/**
 * Collapsible right-side sticky-notes panel.
 * Each user's notes are private (server-side filter by user_id).
 */
export default function StickyNotesPanel() {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = React.useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/sticky-notes`, { withCredentials: true });
      setNotes(r.data || []);
    } catch (e) {
      console.error('Failed to load sticky notes', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const addNote = async (color) => {
    try {
      const r = await axios.post(`${API_URL}/api/sticky-notes`, { color, content: '' }, { withCredentials: true });
      setNotes((prev) => [r.data, ...prev]);
    } catch (e) {
      console.error('Failed', e);
    }
  };

  const updateNote = (id, patch) => {
    setNotes((prev) => prev.map((n) => (n.id === id ? { ...n, ...patch } : n)));
    if (debounceRef.current[id]) clearTimeout(debounceRef.current[id]);
    debounceRef.current[id] = setTimeout(async () => {
      try {
        await axios.put(`${API_URL}/api/sticky-notes/${id}`, patch, { withCredentials: true });
      } catch (e) { console.error('save failed', e); }
    }, 500);
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    setNotes((prev) => prev.filter((n) => n.id !== id));
    try {
      await axios.delete(`${API_URL}/api/sticky-notes/${id}`, { withCredentials: true });
    } catch (e) { console.error('delete failed', e); }
  };

  return (
    <>
      {/* Floating toggle button — always visible bottom-right */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-6 right-6 z-40 w-12 h-12 rounded-full shadow-xl flex items-center justify-center transition-all hover:scale-105"
        style={{ background: '#FEF3C7', border: '2px solid #FBBF24', color: '#92400E' }}
        title="Sticky Notes"
        data-testid="sticky-notes-toggle"
      >
        {open ? <X size={20} weight="bold" /> : <Note size={22} weight="duotone" />}
        {!open && notes.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
            {notes.length}
          </span>
        )}
      </button>

      {/* Slide-in panel */}
      <div
        className={`fixed top-0 right-0 h-full z-30 bg-white shadow-2xl border-l flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}
        style={{ width: 'min(380px, 90vw)' }}
        data-testid="sticky-notes-panel"
      >
        <div className="flex items-center justify-between p-4 border-b" style={{ background: '#FFFBEB' }}>
          <div className="flex items-center gap-2">
            <PushPin size={18} weight="duotone" style={{ color: '#92400E' }} />
            <h3 className="text-base font-bold" style={{ color: '#92400E', fontFamily: 'Chivo, sans-serif' }}>
              Sticky Notes
            </h3>
            <span className="text-[10px] text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Private</span>
          </div>
          <button onClick={() => setOpen(false)} className="text-amber-800 hover:text-amber-900" data-testid="sticky-close">
            <X size={18} />
          </button>
        </div>

        {/* Add new */}
        <div className="px-4 py-3 border-b flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">+ New:</span>
          {COLORS.map((c) => (
            <button
              key={c.id}
              onClick={() => addNote(c.id)}
              className="w-6 h-6 rounded-full border-2 hover:scale-110 transition"
              style={{ background: c.bg, borderColor: c.border }}
              title={`New ${c.id} note`}
              data-testid={`sticky-add-${c.id}`}
            />
          ))}
          <button
            onClick={() => addNote('yellow')}
            className="ml-auto inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-semibold"
            data-testid="sticky-add-default"
          >
            <Plus size={12} /> Add
          </button>
        </div>

        {/* Notes list */}
        <div className="flex-1 overflow-auto p-3 space-y-3" style={{ background: '#F9FAFB' }}>
          {loading ? (
            <div className="text-center text-xs text-gray-400 py-8">Loading…</div>
          ) : notes.length === 0 ? (
            <div className="text-center text-xs text-gray-400 py-12" data-testid="sticky-empty">
              <Note size={40} className="mx-auto mb-2 opacity-30" />
              No sticky notes yet.<br />
              <span className="text-amber-700">Click a colour above to add one.</span>
            </div>
          ) : (
            notes.map((n) => {
              const c = COLOR_BY_ID[n.color] || COLOR_BY_ID.yellow;
              return (
                <div
                  key={n.id}
                  className="rounded-md shadow-sm border-l-4 p-2 transition-all hover:shadow-md"
                  style={{ background: c.bg, borderLeftColor: c.border }}
                  data-testid={`sticky-note-${n.id}`}
                >
                  <textarea
                    className="w-full bg-transparent border-0 outline-none resize-none text-sm min-h-[80px] focus:ring-0"
                    style={{ color: c.dark, fontFamily: 'Chivo, sans-serif' }}
                    placeholder="Type anything… (auto-saves)"
                    value={n.content || ''}
                    onChange={(e) => updateNote(n.id, { content: e.target.value })}
                    data-testid={`sticky-content-${n.id}`}
                  />
                  <div className="flex items-center justify-between mt-1 pt-1 border-t" style={{ borderColor: c.border + '33' }}>
                    <div className="flex gap-1">
                      {COLORS.map((cc) => (
                        <button
                          key={cc.id}
                          onClick={() => updateNote(n.id, { color: cc.id })}
                          className={`w-4 h-4 rounded-full transition ${n.color === cc.id ? 'ring-2 ring-offset-1' : 'opacity-60 hover:opacity-100'}`}
                          style={{ background: cc.bg, border: `1.5px solid ${cc.border}`, ringColor: cc.border }}
                          title={cc.id}
                          data-testid={`sticky-color-${n.id}-${cc.id}`}
                        />
                      ))}
                    </div>
                    <button
                      onClick={() => deleteNote(n.id)}
                      className="text-[10px] opacity-50 hover:opacity-100 hover:text-rose-700 transition"
                      title="Delete note"
                      data-testid={`sticky-delete-${n.id}`}
                    >
                      <Trash size={12} />
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="p-2 text-[10px] text-center text-gray-400 border-t" style={{ background: '#FFFBEB' }}>
          Notes auto-save · Only visible to you
        </div>
      </div>
    </>
  );
}
